import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatPhoneDisplay, toE164, isValidE164 } from '../../../utils/phoneUtils';

/**
 * PhoneNumberManager - Component for managing a list of phone numbers
 *
 * Props:
 * - phoneNumbers: array of phone number objects from database
 * - onSave: callback(updatedPhoneNumbers) when save is clicked
 * - onChange: callback(phoneNumberE164, extension) when current phone number or extension changes
 * - disabled: boolean to disable editing
 * - showDescription: boolean to show/hide description field (default: true)
 * - showSaveButtons: boolean to show/hide save buttons (default: true)
 */
const PhoneNumberManager = ({ phoneNumbers = [], onSave, onChange, disabled = false, showDescription = true, showSaveButtons = true }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneType, setPhoneType] = useState('Office');
  const [description, setDescription] = useState('');
  const [extension, setExtension] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;
  const prevPhoneNumbersLengthRef = useRef(phoneNumbers.length);

  // Phone type options
  const phoneTypes = ['Office', 'Mobile', 'Home', 'Clinic', 'Fax', 'Other'];

  // Load a phone number from the list
  const loadPhoneNumber = useCallback((index) => {
    const phone = phoneNumbers[index];
    if (phone) {
      setSelectedIndex(index);
      setPhoneNumber(formatPhoneDisplay(phone.phone));
      setPhoneType(phone.type || 'Office');
      setDescription(phone.description || '');
      setExtension(phone.extension || '');
      setShowDetails(false);
      setError('');

      // Notify parent of selected phone number and extension
      if (onChange) {
        onChange(phone.phone, phone.extension || ''); // Already in E.164 format from DB
      }
    }
  }, [phoneNumbers, onChange]);

  // Load first phone number on mount, or show form if none exist
  useEffect(() => {
    if (!hasInitialized) {
      setHasInitialized(true);
      if (phoneNumbers.length > 0) {
        loadPhoneNumber(0);
      } else if (!disabled) {
        // Show form directly when no phone numbers exist
        setShowDetails(true);
      }
    }
  }, [phoneNumbers, disabled, loadPhoneNumber, hasInitialized]);

  // Update internal state when phoneNumbers prop changes (e.g., after save).
  useEffect(() => {
    if (hasInitialized && phoneNumbers.length > 0) {
      const currentIndex = selectedIndexRef.current;
      // If we had a selected phone and it still exists, reload it to get updated data
      // BUT: Only if user is not actively editing (showDetails=false)
      // This prevents wiping out unsaved edits when parent component refreshes
      if (currentIndex !== null && phoneNumbers[currentIndex] && !showDetails) {
        const phone = phoneNumbers[currentIndex];
        setPhoneNumber(formatPhoneDisplay(phone.phone));
        setPhoneType(phone.type || 'Office');
        setDescription(phone.description || '');
        setExtension(phone.extension || '');
        // Notify parent of the current phone number
        if (onChange) {
          onChange(phone.phone, phone.extension || '');
        }
      }
      // If selection is out of range (e.g., item was deleted), fall back to first
      else if (currentIndex !== null && currentIndex >= phoneNumbers.length) {
        loadPhoneNumber(0);
      }
      // FIX: If phone numbers become available after initialization (e.g., async load)
      // and currentIndex is null (no selection yet), load first number
      // Only trigger when phone numbers actually arrive (length changed from 0 to >0)
      // This prevents auto-load when user clicks "Add New" which also sets currentIndex to null
      else if (currentIndex === null && prevPhoneNumbersLengthRef.current === 0) {
        loadPhoneNumber(0);
      }
    }
    // Update the ref to track length changes
    prevPhoneNumbersLengthRef.current = phoneNumbers.length;
  }, [phoneNumbers, hasInitialized, showDetails, onChange, loadPhoneNumber]);

  // Handle phone number change - no formatting to avoid cursor jumping
  const handlePhoneChange = (value) => {
    setPhoneNumber(value); // Store raw input
    setShowDetails(true); // Show details when user edits
    setError('');

    // Notify parent of E.164 phone number and extension
    if (onChange) {
      const e164 = toE164(value);
      onChange(e164, extension);
    }
  };

  // Format phone number on blur
  const handlePhoneBlur = () => {
    if (phoneNumber) {
      const e164 = toE164(phoneNumber);
      if (isValidE164(e164)) {
        setPhoneNumber(formatPhoneDisplay(e164));
      }
    }
  };

  // Handle dropdown selection
  const handleSelectPhone = (e) => {
    const index = parseInt(e.target.value);
    if (!isNaN(index)) {
      loadPhoneNumber(index);
    }
  };

  // Handle extension change
  const handleExtensionChange = (value) => {
    setExtension(value);
    // Notify parent of extension change
    if (onChange) {
      const e164 = toE164(phoneNumber);
      onChange(e164, value);
    }
  };

  // Update existing phone number
  const handleUpdate = () => {
    const e164Phone = toE164(phoneNumber);

    if (!isValidE164(e164Phone)) {
      setError('Invalid phone number format');
      return;
    }

    const updatedList = [...phoneNumbers];
    updatedList[selectedIndex] = {
      type: phoneType,
      description,
      phone: e164Phone,
      extension
    };

    onSave(updatedList);
    setShowDetails(false);
    setError('');
  };

  // Save as new phone number
  const handleSaveAsNew = () => {
    const e164Phone = toE164(phoneNumber);

    if (!isValidE164(e164Phone)) {
      setError('Invalid phone number format');
      return;
    }

    const newPhone = {
      type: phoneType,
      description,
      phone: e164Phone,
      extension
    };

    const updatedList = [...phoneNumbers, newPhone];
    onSave(updatedList);
    setSelectedIndex(updatedList.length - 1);
    setShowDetails(false);
    setError('');
  };

  // Check if current values differ from selected phone
  const hasChanges = () => {
    if (selectedIndex === null || !phoneNumbers[selectedIndex]) return true;

    const selected = phoneNumbers[selectedIndex];
    const currentE164 = toE164(phoneNumber);

    return (
      currentE164 !== selected.phone ||
      phoneType !== (selected.type || 'Mobile') ||
      description !== (selected.description || '') ||
      extension !== (selected.extension || '')
    );
  };

  // Handle "Add New" button click
  const handleAddNew = () => {
    setSelectedIndex(null);
    setPhoneNumber('');
    setPhoneType('Mobile');
    setDescription('');
    setExtension('');
    setShowDetails(true);
    setError('');
  };

  // Handle "Edit" button click
  const handleEditSelected = () => {
    if (selectedIndex !== null && phoneNumbers[selectedIndex]) {
      setShowDetails(true);
      setConfirmingDelete(false);
    }
  };

  // Handle delete confirmation
  const handleDelete = () => {
    if (selectedIndex === null) return;
    const updatedList = phoneNumbers.filter((_, i) => i !== selectedIndex);
    onSave(updatedList);
    setConfirmingDelete(false);
    setShowDetails(false);
    setSelectedIndex(null);
    setPhoneNumber('');
    setPhoneType('Office');
    setDescription('');
    setExtension('');
    setError('');
    // If numbers remain, select the first one; otherwise open the blank add form
    if (updatedList.length > 0) {
      // The useEffect will handle selecting index 0 once phoneNumbers updates.
      // Pre-set so there's no flash of empty state.
      setSelectedIndex(0);
    } else if (!disabled) {
      setShowDetails(true);
    }
  };

  return (
    <div>
      {/* Dropdown for saved numbers with action buttons */}
      {phoneNumbers.length > 0 && (
        <>
        <div className="d-flex gap-1 mb-2">
          <select
            className="form-select flex-grow-1"
            value={selectedIndex !== null ? selectedIndex : ''}
            onChange={handleSelectPhone}
            disabled={disabled}
          >
            <option value="">Select from saved numbers...</option>
            {phoneNumbers.map((phoneObj, idx) => (
              <option key={idx} value={idx}>
                {phoneObj.type || 'Unnamed'}: {formatPhoneDisplay(phoneObj.phone)}
                {phoneObj.extension ? ` ext. ${phoneObj.extension}` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleEditSelected}
            disabled={disabled || selectedIndex === null}
            title="Edit selected number"
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            type="button"
            className="btn btn-outline-success"
            onClick={handleAddNew}
            disabled={disabled}
            title="Add new number"
          >
            <i className="fas fa-plus"></i>
          </button>
        </div>
        {!showDetails && description && (<p className='small pl-1 text-muted'><i className="fas fa-info-circle me-1"></i>{description}</p>)}
        </>
      )}

      {/* Error message */}
      {error && (
        <div className="alert alert-danger alert-sm py-1 mb-2">
          <small>{error}</small>
        </div>
      )}

      {/* Expanded details section - show when editing OR when no saved numbers exist (so input doesn't disappear) */}
      {(showDetails || phoneNumbers.length === 0) && (
        <div className="card card-body p-2 mb-2">
          <div className="row g-2">

      {/* Phone number input */}
      <input
        type="tel"
        className="form-control mb-2"
        value={phoneNumber}
        onChange={(e) => handlePhoneChange(e.target.value)}
        onBlur={handlePhoneBlur}
        placeholder="+1 (xxx) xxx-xxxx"
        disabled={disabled}
      />
            <div className="col-6">
              <label className="form-label small mb-1">Type</label>
              <select
                className="form-select form-select-sm"
                value={phoneType}
                onChange={(e) => setPhoneType(e.target.value)}
              >
                {phoneTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="col-6">
              <label className="form-label small mb-1">Extension</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={extension}
                onChange={(e) => handleExtensionChange(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
          {!disabled && showDescription && (
            <div className="mt-2">
              <label className="form-label small mb-1">Description</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Primary contact, Direct line, etc."
              />
            </div>
          )}

          {/* Action buttons - hide when disabled (during call) or when showSaveButtons is false */}
          {!disabled && showSaveButtons && (
            <>
              {confirmingDelete ? (
                <div className="d-flex align-items-center gap-2 mt-2">
                  <small className="text-danger flex-grow-1">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    Delete this number?
                  </small>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={handleDelete}
                  >
                    <i className="fas fa-trash me-1"></i>
                    Delete
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setConfirmingDelete(false)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="d-flex gap-2 mt-2">
                  {selectedIndex !== null && phoneNumbers[selectedIndex] ? (
                    <>
                      <button
                        className="btn btn-sm btn-primary flex-fill"
                        onClick={handleUpdate}
                        disabled={!hasChanges()}
                      >
                        <i className="fas fa-save me-1"></i>
                        Update
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setConfirmingDelete(true)}
                        title="Delete this number"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-sm btn-success flex-fill"
                      onClick={handleSaveAsNew}
                    >
                      <i className="fas fa-plus me-1"></i>
                      Save as New
                    </button>
                  )}
                  {phoneNumbers.length > 0 && (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => { setShowDetails(false); setConfirmingDelete(false); }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PhoneNumberManager;
